import React, { useState } from "react";
import { Close, Fullscreen, FullscreenExit } from "@mui/icons-material";
import {
  Box,
  Dialog,
  DialogActions,
  DialogActionsProps as ActionProps,
  DialogContent,
  DialogContentProps as ContentProps,
  DialogProps,
  DialogTitle,
  DialogTitleProps as TitleProps,
  IconButton,
  PaperProps,
  Theme,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from "classnames";
import modalBg from "svg_icons/modal-header-bg.svg";

const PRIMARY_MODAL_WIDTH = 894;
const SECONDARY_MODAL_WIDTH = 529;

type Variant = "primary" | "secondary" | "default";
interface StyleProps {
  variant?: Variant;
}

const useCustomStyles = makeStyles<Theme, StyleProps>((theme) => ({
  root: ({ variant }) => ({
    "& > div > div": {
      width: variant === "primary" ? PRIMARY_MODAL_WIDTH : SECONDARY_MODAL_WIDTH,
      backgroundImage: variant !== "default" ? `url(${modalBg})` : "none",
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundColor: () => {
        if (variant === "secondary") return theme.palette.background.dialog;

        // if variant is "primary" or "default"
        return theme.palette.common.white;
      },
      maxWidth: "none",
    },
    "&.fullscreen > div > div": {
      width: "100% !important",
      height: "100% !important",
      maxWidth: "100% !important",
      maxHeight: "100% !important",
      margin: 0,
      borderRadius: 0,
    },
  }),
  header: {
    padding: ({ variant }) => (variant === "primary" ? "3rem 4rem" : "2.25rem 2.5rem 1.5rem"),
  },
  headerContent: {
    display: "inline-flex",
    alignItems: "center",
    width: "100%",
  },
  headerIcon: ({ variant }) =>
    variant === "primary"
      ? {
          height: 40,
          width: 40,
          borderRadius: "50%",
          background: theme.palette.secondary.main,
          textAlign: "center",
          marginRight: 15,

          "& > svg": {
            color: theme.palette.common.white,
            height: 40,
            lineHeight: "40px",
          },
        }
      : {
          marginRight: 15,
          "& > svg": {
            color: theme.palette.secondary.main,
            height: 40,
            lineHeight: "40px",
          },
        },
  headerTitleWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    padding: 0,
    fontWeight: "700",
  },
  subtitle: {
    fontSize: 10,
    lineHeight: "16px",
    letterSpacing: 2.5,
    color: theme.palette.secondary.main,
  },
  content: {
    padding: "0 3rem",
  },
  actions: {
    padding: "1.5rem 1.75rem",
  },
}));

/**
 * These come from {@link DialogProps["onClose"]} but are not exported from MUI
 */
type MuiCloseReason = "backdropClick" | "escapeKeyDown";

/**
 * MUI provides its own close reason types ({@link MuiCloseReason}).
 * These additional reasons help us be more specific in close handlers for custom handlers fired from modal actions.
 *
 * Examples:
 * "cancel" - clicking a cancel button.
 * "complete" - clicking a save button or submitting a form and having the action completed successfully.
 */
export type ModalCloseReason = "cancel" | "complete" | MuiCloseReason;

export type ModalCloseHandler<T = unknown> = (
  event: React.MouseEvent<EventTarget> | React.KeyboardEvent<EventTarget> | SubmitEvent,
  reason?: ModalCloseReason,
  data?: T
) => void;

export type ModalProps = Omit<DialogProps, "content" | "onClose"> & {
  /**
   * Dialog actions content
   */
  actions?: React.ReactNode;
  /**
   * className applied to the root dialog
   */
  className?: string;
  variant?: Variant;
  /**
   * Dialog content
   */
  content?: React.ReactNode;
  /**
   * Dialog header content
   */
  header?: React.ReactNode;
  headerIcon?: React.ReactNode;
  headerSubtitle?: string | null;
  /**
   * Mui DialogActionProps
   */
  DialogActionsProps?: ActionProps;
  /**
   * Mui DialogContentProps
   */
  DialogContentProps?: ContentProps;
  /**
   * Mui DialogTitleProps
   */
  DialogTitleProps?: TitleProps;
  /**
   * Mui component used to render the body of the dialog.
   */
  PaperProps?: PaperProps;
  /**
   * Flag to show fullscreen mode toggle button
   * @default false
   */
  showFullscreen?: boolean;
  /**
   * Flag to show close button in header
   * @default false
   */
  showClose?: boolean;
  /**
   * Flag that controls if the onClose should be disabled when clicking outside the open modal. The native modal
   * prop disableBackdropClick is removed in mui@v5, You can find more details about this breaking change in
   * [the migration guide](https://mui.com/guides/migration-v4/#modal). This prop is now used as a custom prop.
   */
  disableBackdropClick?: boolean;
  /**
   * Callback function fired when the modal closes
   */
  onClose?: ModalCloseHandler;
  /**
   * Callback function fired when the fullscreen mode is toggled.
   */
  onFullscreenChange?: (isFullscreen: boolean) => void;
};

const Modal: React.FC<ModalProps> = ({
  actions,
  className,
  variant = "secondary",
  content = null,
  DialogActionsProps,
  DialogContentProps,
  DialogTitleProps,
  disableBackdropClick = false,
  header = null,
  headerIcon = null,
  headerSubtitle = null,
  open,
  onClose,
  onFullscreenChange,
  showFullscreen = false,
  showClose = false,
  ...dialogProps
}: ModalProps) => {
  const classes = useCustomStyles({ variant });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleOnClose: ModalCloseHandler = (event, reason) => {
    if (!disableBackdropClick || reason !== "backdropClick") {
      onClose?.(event, reason);
    }
  };

  const handleToggleFullscreen = () => {
    setIsFullscreen((prev) => {
      const next = !prev;
      onFullscreenChange?.(next);
      return next;
    });
  };

  return (
    <Dialog {...dialogProps} open={open} className={classNames(classes.root, className, { fullscreen: isFullscreen })} onClose={handleOnClose}>
      <Box className={classes.header}>
        <Box className={classes.headerContent}>
          {headerIcon && <Box className={classes.headerIcon}>{headerIcon}</Box>}
          <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <Box className={classes.headerTitleWrapper}>
              <DialogTitle className={classes.title} {...DialogTitleProps}>
                {header}
              </DialogTitle>
              {headerSubtitle && <span className={classNames("subtitle", classes.subtitle)}>{headerSubtitle}</span>}
            </Box>
            {(showFullscreen || showClose) && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  backgroundColor: "white",
                  borderRadius: 1,
                  padding: 0.5,
                  boxShadow: 1,
                  position: "absolute",
                  top: 0,
                  right: 0,
                }}
              >
                {showFullscreen && (
                  <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                    <IconButton onClick={handleToggleFullscreen} size="small" aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                      {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
                    </IconButton>
                  </Tooltip>
                )}
                {showClose && (
                  <Tooltip title="Close">
                    <IconButton onClick={onClose} size="small" aria-label="Close">
                      <Close />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <DialogContent
        {...DialogContentProps}
        className={classNames(DialogContentProps?.className, "customScrollbars", classes.content, { greyBackground: variant === "secondary" })}
      >
        {content}
      </DialogContent>

      <DialogActions className={classes.actions} {...DialogActionsProps}>
        {actions}
      </DialogActions>
    </Dialog>
  );
};

export default Modal;
